import { formatDurationLabel } from "@/shared/duration.ts";

// Re-exported so the many callers of this module keep one import site for
// the time pickers and the duration label they show together.
export { formatDurationLabel };

export type TimeOption = { value: string; label: string };

/** Format a "HH:mm" 24h value as a "h:mm AM/PM" label (used in dropdown lists). */
export function formatTimeLabel(value: string): string {
  const [h, m] = value.split(":").map(Number);
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? "AM" : "PM";
  return `${hour12}:${String(m).padStart(2, "0")} ${ampm}`;
}

/**
 * Like formatTimeLabel but drops the ":00" on whole hours ("6 AM" vs "6:00 AM").
 * Used only for the collapsed picker pill, where the tighter form reads better;
 * the dropdown keeps the full "6:00 AM".
 */
export function formatTimeLabelCompact(value: string): string {
  const [h, m] = value.split(":").map(Number);
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? "AM" : "PM";
  return m === 0 ? `${hour12} ${ampm}` : `${hour12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function generateTimeOptions(): TimeOption[] {
  const options: TimeOption[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      options.push({ value, label: formatTimeLabel(value) });
    }
  }
  return options;
}

/** The fixed 15-minute grid of selectable times (00:00 … 23:45). */
export const TIME_OPTIONS: TimeOption[] = generateTimeOptions();

/**
 * The 15-minute grid (TIME_OPTIONS) with `value` injected if it isn't already a
 * grid slot. Travel buffers (and externally-synced events) routinely sit on
 * off-grid minutes like 18:40; without this the <Select> has no matching option
 * and <SelectValue /> renders blank. Returns TIME_OPTIONS unchanged when on-grid.
 */
export function timeOptionsWithValue(value: string): TimeOption[] {
  if (TIME_OPTIONS.some((o) => o.value === value)) return TIME_OPTIONS;
  const injected = { value, label: formatTimeLabel(value) };
  const idx = TIME_OPTIONS.findIndex((o) => o.value > value);
  if (idx === -1) return [...TIME_OPTIONS, injected];
  return [...TIME_OPTIONS.slice(0, idx), injected, ...TIME_OPTIONS.slice(idx)];
}

const MINUTES_IN_DAY = 24 * 60;

/** Parse "HH:mm" into minutes since midnight. */
export function timeStringToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

/** Format minutes-since-midnight (wraps across days) back to "HH:mm". */
export function minutesToTimeString(mins: number): string {
  const wrapped = ((mins % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export type EndTimeOption = { value: string; label: string; nextDay: boolean };

/**
 * End-time options for a timed event, mirroring Google Calendar: every 15-min
 * slot strictly *after* `start`, spanning up to 24h later so an event can cross
 * midnight (next-day slots are flagged + labeled). Each option shows its
 * duration from start. The current end is always included even when it sits off
 * the 15-min grid (e.g. 18:40) so the <Select> can render it. Earlier-than-start
 * times are omitted entirely — that's what keeps end ≥ start by construction.
 */
export function endTimeOptions(
  start: string,
  currentEnd: string,
  currentEndNextDay: boolean,
): EndTimeOption[] {
  const startMins = timeStringToMinutes(start);
  const options: EndTimeOption[] = [];
  for (let offset = 15; offset < MINUTES_IN_DAY; offset += 15) {
    const abs = startMins + offset;
    const nextDay = abs >= MINUTES_IN_DAY;
    const value = minutesToTimeString(abs);
    options.push({
      value,
      label: `${formatTimeLabel(value)} (${formatDurationLabel(offset)})${nextDay ? " (next day)" : ""}`,
      nextDay,
    });
  }
  // Each clock value appears once per <24h window, so an on-grid current end is
  // already present; only off-grid ends need injecting (in duration order).
  if (!options.some((o) => o.value === currentEnd)) {
    const curAbs = timeStringToMinutes(currentEnd) + (currentEndNextDay ? MINUTES_IN_DAY : 0);
    const dur = curAbs - startMins;
    const positiveDur = dur > 0 ? dur : dur + MINUTES_IN_DAY;
    const injected: EndTimeOption = {
      value: currentEnd,
      label: `${formatTimeLabel(currentEnd)} (${formatDurationLabel(positiveDur)})${currentEndNextDay ? " (next day)" : ""}`,
      nextDay: currentEndNextDay,
    };
    const idx = options.findIndex(
      (o) => timeStringToMinutes(o.value) + (o.nextDay ? MINUTES_IN_DAY : 0) > curAbs,
    );
    if (idx === -1) options.push(injected);
    else options.splice(idx, 0, injected);
  }
  return options;
}
